import { useMemo, useState } from 'react';
import { MatrixBand, findMatrixBand } from '../HedgePlans/HedgePlanMatrixBands';

// Which decile bands the chart shades and the table highlights, driven by hovering and clicking, as on the Hedge Plan screen.
export function useSeasonalMatrixBandSelection(bands: MatrixBand[], lastClosePrice: number | undefined) {
  const [pinnedPercentiles, setPinnedPercentiles] = useState<Set<number>>(new Set());
  const [hoveredBand, setHoveredBand] = useState<MatrixBand | undefined>(undefined);
  const [hoveredPrice, setHoveredPrice] = useState<number | undefined>(undefined);

  // A hovered band shows alongside the pinned ones; with nothing hovered the pinned bands show;
  // with nothing pinned either, the band holding the last close shows.
  const chartedBands = useMemo<MatrixBand[]>(() => {
    const pinnedBands = bands.filter((band) => pinnedPercentiles.has(band.percentile));
    if (hoveredBand) {
      return pinnedPercentiles.has(hoveredBand.percentile) ? pinnedBands : [...pinnedBands, hoveredBand];
    }
    if (pinnedBands.length > 0) {
      return pinnedBands;
    }
    const lastCloseBand = findMatrixBand(bands, lastClosePrice);
    return lastCloseBand ? [lastCloseBand] : [];
  }, [bands, pinnedPercentiles, hoveredBand, lastClosePrice]);

  // The table highlights the band holding the price under the cursor, or the last close.
  const highlightedPercentile = findMatrixBand(bands, hoveredPrice ?? lastClosePrice)?.percentile;

  function toggleBand(band: MatrixBand) {
    setPinnedPercentiles((current) => {
      const next = new Set(current);
      if (next.has(band.percentile)) {
        next.delete(band.percentile);
      } else {
        next.add(band.percentile);
      }
      return next;
    });
  }

  // Hovering the price line mirrors hovering a cell: it highlights the band in the table and previews it on the chart.
  function hoverPrice(price: number | undefined) {
    setHoveredPrice(price);
    setHoveredBand(price === undefined ? undefined : findMatrixBand(bands, price));
  }

  return {
    chartedBands,
    highlightedPercentile,
    pinnedPercentiles,
    toggleBand,
    hoverBand: setHoveredBand,
    hoverPrice,
  };
}
